import { ApplicationStore } from "../store/ApplicationStore";
import { CompositionService } from "../services/CompositionService";
import { getScriptInsertionTargetFingerprint } from "./scriptInsertionTargetFingerprint";

export interface BeginScriptAddMediaFlowParams {
    atClipLocalSec: number;
    preferExistingEmptyScene?: boolean;
    source?: string;
    sceneIndex?: number;
    sceneStartSec?: number;
    sceneEndSec?: number;
}

export interface BeginScriptInsertMediaAtCursorFlowParams {
    atClipLocalSec: number;
    atTimelineSec?: number;
    source?: string;
}

const createRequestIdentity = (): {
    requestId: string;
    expectedScriptClipId?: string;
    expectedCompositionSessionVersion?: number;
    expectedScriptTimingFingerprint?: string;
} => {
    let expectedScriptClipId: string | undefined;
    let expectedCompositionSessionVersion: number | undefined;
    let expectedScriptTimingFingerprint: string | undefined;
    try {
        if (CompositionService.isInitialized()) {
            const scriptClip = CompositionService.getScriptLayerClip?.();
            expectedScriptClipId = String(scriptClip?.id ?? "").trim() || undefined;
            const version = Number(CompositionService.getSessionVersion?.());
            expectedCompositionSessionVersion = Number.isFinite(version) ? version : undefined;
            expectedScriptTimingFingerprint =
                getScriptInsertionTargetFingerprint(scriptClip) || undefined;
        }
    } catch {}

    return {
        requestId:
            globalThis.crypto?.randomUUID?.() ??
            `script_insert_${Date.now()}_${Math.random()}`,
        expectedScriptClipId,
        expectedCompositionSessionVersion,
        expectedScriptTimingFingerprint,
    };
};

const openProjectFiles = (source: string): void => {
    try {
        window.dispatchEvent(
            new CustomEvent("openProjectSidebarFiles", {
                detail: { source },
            }),
        );
    } catch {}
};

export const beginScriptAddMediaFlow = (params: BeginScriptAddMediaFlowParams): void => {
    const atClipLocalSec = Number(params?.atClipLocalSec ?? NaN);
    if (!Number.isFinite(atClipLocalSec) || atClipLocalSec < 0) return;

    ApplicationStore.setPendingInsertIntoScriptContext({
        ...createRequestIdentity(),
        atClipLocalSec,
        preferExistingEmptyScene: Boolean(params?.preferExistingEmptyScene),
        mode: "add_to_scene",
        source: typeof params?.source === "string" ? params.source : undefined,
        sceneIndex:
            Number.isFinite(Number(params?.sceneIndex)) && Number(params?.sceneIndex) >= 0
                ? Math.floor(Number(params.sceneIndex))
                : undefined,
        sceneStartSec:
            Number.isFinite(Number(params?.sceneStartSec)) && Number(params?.sceneStartSec) >= 0
                ? Number(params.sceneStartSec)
                : undefined,
        sceneEndSec:
            Number.isFinite(Number(params?.sceneEndSec)) && Number(params?.sceneEndSec) >= 0
                ? Number(params.sceneEndSec)
                : undefined,
    });

    openProjectFiles(params?.source ?? "add-media");
};

/**
 * Open Project Files for a transcript-cursor insertion. Audio/video selected
 * in this mode become a new ScriptClip scene; images remain ordinary B-roll.
 */
export const beginScriptInsertMediaAtCursorFlow = (
    params: BeginScriptInsertMediaAtCursorFlowParams,
): void => {
    const atClipLocalSec = Number(params?.atClipLocalSec ?? NaN);
    if (!Number.isFinite(atClipLocalSec) || atClipLocalSec < 0) return;

    ApplicationStore.setPendingInsertIntoScriptContext({
        ...createRequestIdentity(),
        atClipLocalSec,
        atTimelineSec: params.atTimelineSec,
        preferExistingEmptyScene: false,
        mode: "insert_into_script",
        source:
            typeof params?.source === "string"
                ? params.source
                : "transcript-cursor-add-media",
    });

    openProjectFiles(params?.source ?? "transcript-cursor-add-media");
};
